<template>
  <v-container fluid :class="baseStore?.isDesktop ? 'pa-8' : 'pa-4'">
    <v-row justify="center">
      <v-col cols="12" md="7" lg="6">

        <div v-if="loading" class="text-center py-16">
          <v-progress-circular indeterminate color="#3d1b70" size="48" />
          <p class="text-body-1 mt-4 zert-soft">Ihr Zertifikat wird geladen ...</p>
        </div>

        <v-card
          v-else-if="zertifikat"
          elevation="2"
          class="zert-card"
          style="border-radius: 0; border: 1px solid #e5e7eb; background: #ffffff; overflow: hidden;"
        >
          <!-- Kopfbereich in PickPoint-Lila mit Gold-Akzent -->
          <div class="zert-header text-center">
            <img class="zert-logo" src="@/assets/pickpointlogo.png" alt="PickPoint" />
            <v-icon color="#fcc25e" size="52" class="mt-2">mdi-certificate</v-icon>
            <h1 class="zert-header-title mt-3">Herzlichen Glückwunsch!</h1>
            <p class="zert-header-sub mb-0">
              Hiermit bestätigen wir die erfolgreiche Teilnahme am Live-Webinar
            </p>
          </div>
          <div class="zert-gold-bar"></div>

          <v-card-text class="pa-8 text-center">
            <div class="zert-name">{{ zertifikat.teilnehmer_name }}</div>
            <div v-if="zertifikat.praxis" class="text-body-2 zert-soft mt-1">{{ zertifikat.praxis }}</div>

            <div class="zert-ornament my-5">
              <span class="zert-ornament-line"></span>
              <v-icon color="#fcc25e" size="18">mdi-star-four-points</v-icon>
              <span class="zert-ornament-line"></span>
            </div>

            <div class="text-body-2 zert-soft text-uppercase" style="letter-spacing: 1.5px;">Webinar</div>
            <div class="zert-webinar mt-1">{{ zertifikat.webinar_title }}</div>
            <div v-if="zertifikat.untertitel" class="text-body-2 zert-soft mt-1">
              {{ zertifikat.untertitel }}
            </div>

            <!-- Dozent mit Bild + Termin -->
            <div class="zert-meta-row mt-6">
              <div v-if="zertifikat.dozent_name" class="zert-meta-box">
                <v-avatar v-if="dozentImage" size="72" class="zert-dozent-avatar">
                  <v-img :src="dozentImage" :alt="zertifikat.dozent_name" cover />
                </v-avatar>
                <v-avatar v-else size="72" color="#ede9f5" class="zert-dozent-avatar">
                  <v-icon color="#3d1b70" size="40">mdi-account-tie</v-icon>
                </v-avatar>
                <div class="text-caption zert-soft text-uppercase mt-2" style="letter-spacing: 1px;">Dozent</div>
                <div class="text-body-1 font-weight-bold zert-purple">{{ zertifikat.dozent_name }}</div>
              </div>
              <div class="zert-meta-box">
                <v-avatar size="72" color="#ede9f5" class="zert-dozent-avatar">
                  <v-icon color="#3d1b70" size="36">mdi-calendar-check</v-icon>
                </v-avatar>
                <div class="text-caption zert-soft text-uppercase mt-2" style="letter-spacing: 1px;">Termin</div>
                <div class="text-body-1 font-weight-bold zert-purple">{{ zertifikat.termin_datum }}</div>
              </div>
            </div>

            <v-btn
              class="zert-download-btn mt-8"
              :class="{ 'zert-download-btn-busy': downloading }"
              color="#3d1b70"
              size="x-large"
              block
              @click="downloadPdf"
            >
              <template v-if="!downloading">
                <v-icon start>mdi-download</v-icon>
                Teilnahmezertifikat herunterladen (PDF)
              </template>
              <template v-else-if="downloadStatus === 'generating'">
                <v-progress-circular indeterminate size="22" width="2" color="#fcc25e" class="mr-3" />
                Ihr Zertifikat wird generiert …
              </template>
              <template v-else>
                <v-icon start color="#fcc25e">mdi-check-circle</v-icon>
                Zertifikat wird heruntergeladen …
              </template>
            </v-btn>

            <p v-if="downloadError" class="text-body-2 mt-3 mb-0" style="color: #b91c1c;">
              Der Download hat leider nicht geklappt. Bitte versuchen Sie es noch einmal
              oder wenden Sie sich an service@teamwaizmann.de.
            </p>

            <p class="text-caption zert-soft mt-4 mb-0">
              Ein Angebot von TeamWaizmann · www.pickpoint.live
            </p>
          </v-card-text>
        </v-card>

        <v-card
          v-else
          elevation="2"
          style="border-radius: 0; border: 1px solid #e5e7eb; background: #ffffff; overflow: hidden;"
        >
          <div class="zert-header text-center">
            <img class="zert-logo" src="@/assets/pickpointlogo.png" alt="PickPoint" />
            <v-icon color="#fcc25e" size="52" class="mt-2">mdi-certificate-outline</v-icon>
            <h1 class="zert-header-title mt-3">Zertifikat nicht verfügbar</h1>
          </div>
          <div class="zert-gold-bar"></div>
          <v-card-text class="pa-8 text-center">
            <p class="text-body-1 zert-soft mt-2" v-if="reason === 'not_attended'">
              Für diese Anmeldung liegt uns leider keine bestätigte Webinar-Teilnahme vor.
              Zertifikate stellen wir nur für tatsächlich besuchte Webinare aus.
            </p>
            <p class="text-body-1 zert-soft mt-2" v-else>
              Dieser Zertifikat-Link ist ungültig oder abgelaufen.
              Bitte prüfen Sie den Link aus Ihrer E-Mail.
            </p>
            <p class="text-body-2 zert-soft mt-4">
              Bei Fragen erreichen Sie uns unter
              <a href="mailto:service@teamwaizmann.de">service@teamwaizmann.de</a>.
            </p>
          </v-card-text>
        </v-card>

      </v-col>

      <!-- Waizmann-Rechteck rechts neben der Karte (mobil darunter) -->
      <v-col v-if="!loading" cols="12" md="3" lg="2" class="zert-right">
        <p class="text-body-2 zert-soft mb-3 zert-right-text">
          Dieses Webinar wurde unterstützt durch die <b>WaizmannTabelle</b> — das Original seit 1995.
        </p>
        <a
          class="waizmann-box mx-auto"
          href="https://www.teamwaizmann.de"
          target="_blank"
          rel="noopener"
        >
          <div class="waizmann-box-mark">®</div>
          <div class="waizmann-box-title">WaizmannTabellen anfordern</div>
          <div class="waizmann-box-footer">
            <div class="waizmann-box-original">das Original</div>
            <div class="waizmann-box-since">seit 1995</div>
          </div>
        </a>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
import { inject, onMounted, ref, computed } from 'vue';
import { useRoute } from 'vue-router';
import http from '@/services/http.js';

const baseCalls = inject('baseCalls');
const baseStore = inject('baseStore');
const updateDocumentHead = inject('updateDocumentHead');

const route = useRoute();
const loading = ref(true);
const zertifikat = ref(null);
const reason = ref('');

updateDocumentHead?.(
  'PickPoint - Teilnahmezertifikat',
  'Ihr Teilnahmezertifikat zum PickPoint Webinar'
);

const pdfUrl = computed(() =>
  `${http.defaults.baseURL}/zertifikatpdf/${route.params.teilnahmeid}/${route.params.hash}`
);

const downloading = ref(false);
const downloadStatus = ref('');
const downloadError = ref(false);

async function downloadPdf() {
  if (downloading.value) return;
  downloading.value = true;
  downloadStatus.value = 'generating';
  downloadError.value = false;
  try {
    const response = await fetch(pdfUrl.value, { credentials: 'include' });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const blob = await response.blob();

    downloadStatus.value = 'done';
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = 'Teilnahmezertifikat_PickPoint.pdf';
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
    setTimeout(() => {
      downloading.value = false;
      downloadStatus.value = '';
    }, 2500);
  } catch (error) {
    console.error('Fehler beim Zertifikat-Download:', error);
    downloadError.value = true;
    downloading.value = false;
    downloadStatus.value = '';
  }
}

function resolveDozentImage(imageName) {
  if (!imageName) return null;
  if (typeof imageName !== 'string') return null;
  if (imageName.startsWith('/')) return imageName;

  try {
    return require(`@/assets/dozenten/${imageName}`);
  } catch (e) {
    return `/dozenten/${imageName}`;
  }
}

const dozentImage = computed(() => resolveDozentImage(zertifikat.value?.dozent_image));

onMounted(async () => {
  try {
    const response = await baseCalls.zertifikatData(route.params.teilnahmeid, route.params.hash);
    if (response?.data?.ok) {
      zertifikat.value = response.data;
    } else {
      reason.value = response?.data?.reason || 'not_found';
    }
  } catch (error) {
    console.error('Fehler beim Laden des Zertifikats:', error);
    reason.value = 'not_found';
  } finally {
    loading.value = false;
  }
});
</script>

<style scoped>
.zert-header {
  background: linear-gradient(135deg, #3d1b70 0%, #2a1150 100%);
  padding: 32px 24px 28px;
}

.zert-logo {
  height: 34px;
  display: block;
  margin: 0 auto;
  filter: brightness(0) invert(1);
}

.zert-gold-bar {
  height: 5px;
  background: linear-gradient(90deg, #fcc25e 0%, #f5a623 50%, #fcc25e 100%);
}

.zert-header-title {
  margin: 0;
  color: #ffffff;
  font-size: clamp(1.4rem, 2.5vw, 1.9rem);
  font-weight: 700;
}

.zert-header-sub {
  color: rgba(255, 255, 255, 0.85);
  margin-top: 6px;
  font-size: 0.95rem;
}

.zert-name {
  color: #3d1b70;
  font-size: clamp(1.4rem, 2.4vw, 1.8rem);
  font-weight: 700;
}

.zert-webinar {
  color: #3d1b70;
  font-size: clamp(1.05rem, 1.8vw, 1.3rem);
  font-weight: 700;
}

.zert-purple {
  color: #3d1b70;
}

.zert-soft {
  color: #6b7280;
}

.zert-ornament {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.zert-ornament-line {
  display: inline-block;
  width: 72px;
  height: 2px;
  background: linear-gradient(90deg, transparent, #fcc25e);
}

.zert-ornament-line:last-child {
  background: linear-gradient(90deg, #fcc25e, transparent);
}

.zert-meta-row {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 40px;
}

.zert-meta-box {
  min-width: 140px;
}

.zert-dozent-avatar {
  border: 3px solid #fcc25e;
}

/* Waizmann-Rechteck — Original-Optik wie auf der Startseite, rechts neben der Karte */
.zert-right {
  align-self: start;
  position: sticky;
  top: 120px;
}

.zert-right-text {
  max-width: 200px;
  margin-left: auto;
  margin-right: auto;
}

@media (max-width: 959px) {
  .zert-right {
    position: static;
    top: auto;
    text-align: center;
  }
}

.waizmann-box {
  background: #3166A5;
  color: #ffffff;
  border-radius: 4px;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  aspect-ratio: 105 / 168;
  position: relative;
  text-decoration: none;
  width: min(200px, 100%);
  text-align: left;
  box-shadow: none;
}

.waizmann-box:hover {
  box-shadow: none;
}

.waizmann-box-mark {
  position: absolute;
  top: 6px;
  right: 8px;
  font-size: 12px;
  font-weight: 700;
  opacity: 0.9;
}

.waizmann-box-title {
  font-size: 18px;
  font-weight: 800;
  line-height: 1.2;
}

.waizmann-box-footer {
  position: absolute;
  right: 10px;
  bottom: 10px;
  text-align: right;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.waizmann-box-original {
  font-size: 16px;
  font-weight: 700;
}

.waizmann-box-since {
  font-size: 14px;
  opacity: 0.9;
}

.zert-download-btn {
  min-height: 56px;
  font-size: 17px;
  font-weight: 700;
  letter-spacing: 0.2px;
}

/* Lade-Zustand: Klicks aus, aber in Markenfarbe statt Vuetify-Grau */
.zert-download-btn-busy {
  pointer-events: none;
  opacity: 0.92;
}

.v-card,
.v-btn {
  border-radius: 0 !important;
}
</style>
